import React from 'react'
import { OutlinedSelect } from 'components'
import {
  TextField,
  SelectChangeEvent,
  InputAdornment,
  Stack,
  Alert,
  Link,
  FormHelperText,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'

import { FormattedMessage } from 'react-intl'
import intl from 'localization/components'
import { ReactNode } from 'react'
import { SubscriptionPlan } from 'types'
import { planLabel } from 'utils/plan-label'
import { packageDefaultsNote } from 'utils/trial-mcp'

// Plan options offered in the Create Customer dialog.
// Must stay in sync with ACTIVE_SUBSCRIPTION_PLANS (src/types/global.ts) so
// admins can only create customers on currently-sold packages. Existing
// customers on legacy plans are handled separately by KnownSubscriptionPlan.
export const CREATE_CUSTOMER_PLAN_OPTIONS: {
  value: SubscriptionPlan
  text: string
}[] = [
  // ENG2-2454 (T1): labels are the names Sales uses (planLabel); the value
  // sent to the API stays the enum.
  { value: 'TRIAL_V2', text: planLabel('TRIAL_V2') },
  { value: 'TRIAL_MCP', text: planLabel('TRIAL_MCP') },
  { value: 'MANUAL_PACKAGE', text: planLabel('MANUAL_PACKAGE') },
  { value: 'STRATEGIC_PACKAGE', text: planLabel('STRATEGIC_PACKAGE') },
]

interface CreateCustomerState {
  country: string
  local_id: string
  name: string
  plan: string
  responsible_user_id: string | number | undefined
}

interface ResponsibleOption {
  text: string | ReactNode
  value: string | number
}

interface CreateCompanyProps {
  handleCreateCustomerChange: (
    type: string,
  ) => (
    event:
      | React.ChangeEvent<HTMLInputElement | { value: unknown }>
      | SelectChangeEvent<string | number>,
  ) => void
  createCustomerState: CreateCustomerState
  messages: Record<string, any>
  responsibleOptions: ResponsibleOption[]
  validate: { local_id: boolean }
  existingCustomer?: {
    id: number
    name: string
    local_id: string
    country: string
  } | null
}

const CreateCompany = ({
  handleCreateCustomerChange,
  createCustomerState,
  messages,
  responsibleOptions,
  validate,
  existingCustomer,
}: CreateCompanyProps) => {
  const hasLocalIdInput = createCustomerState.local_id.length > 0

  return (
    <Stack spacing={2}>
      {existingCustomer ? (
        // Creating it again would either be rejected by CMAPI or — before
        // ENG2-2325 — leave a duplicate behind. Send the operator to the
        // customer that already exists instead.
        <Alert severity="warning">
          {existingCustomer.name} ({existingCustomer.country}{' '}
          {existingCustomer.local_id}) is already a customer.{' '}
          <Link href={`/customer/${existingCustomer.id}`}>
            Open customer {existingCustomer.id}
          </Link>
        </Alert>
      ) : null}

      <OutlinedSelect
        onChange={handleCreateCustomerChange('country')}
        value={createCustomerState.country}
        name="country"
        label={messages[intl.customerManagement('country')]}
        options={[
          { value: 'DK', text: 'Denmark (DK)' },
          { value: 'SE', text: 'Sweden (SE)' },
          { value: 'NO', text: 'Norway (NO)' },
          { value: 'FI', text: 'Finland (FI)' },
        ]}
      />

      <TextField
        value={createCustomerState.local_id}
        name="company_id"
        label={messages[intl.generic('cvr-number')]}
        onChange={handleCreateCustomerChange('local_id')}
        variant="outlined"
        required
        error={hasLocalIdInput && !validate.local_id}
        helperText={
          hasLocalIdInput && !validate.local_id
            ? 'Please enter a valid CVR/Org number'
            : ' '
        }
        InputProps={{
          endAdornment: hasLocalIdInput ? (
            <InputAdornment position="end">
              {validate.local_id ? (
                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
              ) : (
                <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />
              )}
            </InputAdornment>
          ) : null,
        }}
      />

      <TextField
        name="name"
        value={createCustomerState.name}
        label={messages[intl.generic('name')]}
        onChange={handleCreateCustomerChange('name')}
        variant="outlined"
        disabled
        helperText="Auto-filled from registry"
        sx={{
          '& .MuiInputBase-input.Mui-disabled': {
            WebkitTextFillColor: (theme) =>
              theme.palette.mode === 'dark' ? '#fff' : 'inherit',
          },
          '& .MuiOutlinedInput-root.Mui-disabled': {
            bgcolor: 'action.hover',
          },
        }}
      />

      <OutlinedSelect
        onChange={handleCreateCustomerChange('plan')}
        value={createCustomerState.plan}
        id="customer_plan"
        name="customer_plan"
        label={
          <FormattedMessage id={intl.customerManagement('customer-plan')} />
        }
        options={CREATE_CUSTOMER_PLAN_OPTIONS}
      />
      {/* ENG2-2614: what RisikaAPI fills in on its own for this plan (the
          TRIAL_MCP Risika for AI trial), so the operator sees it up front. */}
      {packageDefaultsNote(createCustomerState.plan) && (
        <FormHelperText sx={{ mt: -1 }} data-testid="package-defaults-note">
          {packageDefaultsNote(createCustomerState.plan)}
        </FormHelperText>
      )}

      <OutlinedSelect
        onChange={handleCreateCustomerChange('responsible_user_id')}
        value={createCustomerState.responsible_user_id}
        id="responsible_user"
        name="responsible_user"
        label={messages[intl.customerManagement('responsible-person')]}
        options={responsibleOptions}
      />
    </Stack>
  )
}

export default CreateCompany
